import {
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { cn } from "@/ui/lib/utils";
import {
  BUTTON_DEFAULTS,
  BUTTON_VARIANT_CLASS_MAP,
  BUTTON_WIDTH_CLASS_MAP,
  TButtonProps,
} from "@/ui/components/primitives/Button/Button.config";
import { ButtonChildren } from "./ButtonChildren";
import { getButtonSizeClasses } from "./Button.utils";
import { DisplayState } from "@/constants/state";

type TButtonChildProps = {
  children?: ReactNode;
  className?: string;
  "data-component"?: string;
  ref?: Ref<HTMLElement>;
};


export const Button = forwardRef<HTMLElement, TButtonProps>(
  (
    {
      variant = BUTTON_DEFAULTS.variant,
      size = BUTTON_DEFAULTS.size,
      asChild = BUTTON_DEFAULTS.asChild,
      width = BUTTON_DEFAULTS.width,
      children,
      className,
      type = "button",
      state,
      ...props
    },
    ref,
  ) => {
    const buttonClassName = cn(
      "inline-flex items-center justify-center gap-2 whitespace-nowrap",
      "rounded-sys-full",
      "border-solid",
      "transition-all duration-200",
      "focus-visible:outline-none focus-visible:ring-2",
      "disabled:pointer-events-none disabled:opacity-50",
      "[&_div]:pointer-events-none",
      "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      BUTTON_VARIANT_CLASS_MAP[variant],
      BUTTON_WIDTH_CLASS_MAP[width],
      getButtonSizeClasses(variant, size),
      className,
    );

    if (asChild && isValidElement(children)) {
      const child = children as ReactElement<TButtonChildProps>;

      return cloneElement(child, {
        ...props,
        ref,
        "data-component": "Button",
        className: cn(buttonClassName, child.props.className),
        children: (
          <ButtonChildren state={state}>
            { child.props.children }
          </ButtonChildren>
        ),
      });
    }

    return (
      <button
        {...props}
        ref={ref as Ref<HTMLButtonElement>}
        type={type}
        data-component="Button"
        className={buttonClassName}
        disabled={state === DisplayState.LOADING || props.disabled}
      >
        <ButtonChildren state={state}>{ children }</ButtonChildren>
      </button>
    );
  },
);

Button.displayName = "Button";
